import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { notifyWorker } from '../../lib/notifyWorker'

function fmtDate(s) {
  if (!s) return ''
  const d = new Date(s + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' })
}

export default function DayOffApprovalPanel() {
  const { profile, isOwner } = useAuth()
  const canApprove = isOwner || profile?.role === 'office_staff'
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsed] = useState(false)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('day_off_requests')
      .select('*, profiles:user_id(full_name, role, business_ids)')
      .eq('status', 'pending')
      .order('request_date', { ascending: true })
    setRequests(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const approve = async (req, autoAssign = false) => {
    if (!canApprove) return
    setBusyId(req.id)

    // Update the day_off_requests row
    await supabase.from('day_off_requests').update({
      status: 'approved',
      approved_by: profile.id,
      approved_at: new Date().toISOString(),
    }).eq('id', req.id)

    // Set the worker's schedule row for that date to day_off
    const { data: existing } = await supabase.from('worker_schedule')
      .select('id')
      .eq('user_id', req.user_id)
      .eq('schedule_date', req.request_date)
      .limit(1)
      .maybeSingle()
    if (existing?.id) {
      await supabase.from('worker_schedule').update({
        status: 'day_off', job_id: null, updated_at: new Date().toISOString(),
      }).eq('id', existing.id)
    } else {
      await supabase.from('worker_schedule').insert({
        user_id: req.user_id,
        schedule_date: req.request_date,
        status: 'day_off',
        assigned_by: profile.id,
      })
    }

    // Auto-assign a replacement = most junior available in-season worker
    let replacementName = null
    if (autoAssign) {
      const { data: pool } = await supabase.from('profiles')
        .select('id, full_name, role, seniority_rank, max_days_per_week, season_start, season_end, availability_status')
        .neq('role', 'store_partner')
        .eq('is_active', true)
      const today = req.request_date
      const available = (pool || []).filter((w) => {
        if (w.availability_status !== 'available') return false
        if (w.id === req.user_id) return false
        if (w.season_start && today < w.season_start) return false
        if (w.season_end && today > w.season_end) return false
        return true
      })

      // Check existing schedule and day-off state for that date
      const { data: existingDay } = await supabase.from('worker_schedule')
        .select('user_id, status')
        .eq('schedule_date', today)
      const busyIds = new Set((existingDay || [])
        .filter((r) => ['scheduled', 'called_off', 'no_call_no_show'].includes(r.status))
        .map((r) => r.user_id))

      // Week cap
      const weekStart = (() => {
        const d = new Date(today + 'T00:00:00')
        const dow = d.getDay()
        d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow))
        return d.toISOString().slice(0, 10)
      })()
      const weekEnd = (() => {
        const d = new Date(weekStart + 'T00:00:00')
        d.setDate(d.getDate() + 5)
        return d.toISOString().slice(0, 10)
      })()
      const { data: weekRows } = await supabase.from('worker_schedule')
        .select('user_id, status')
        .gte('schedule_date', weekStart)
        .lte('schedule_date', weekEnd)
      const weekCount = {}
      for (const r of (weekRows || [])) {
        if (r.status === 'scheduled') weekCount[r.user_id] = (weekCount[r.user_id] || 0) + 1
      }

      const candidates = available
        .filter((w) => !busyIds.has(w.id))
        .filter((w) => (weekCount[w.id] || 0) < (w.max_days_per_week || 6))
        .sort((a, b) => {
          const da = weekCount[a.id] || 0, db = weekCount[b.id] || 0
          if (da !== db) return da - db
          const ra = a.seniority_rank || 999, rb = b.seniority_rank || 999
          return rb - ra // most junior first
        })

      const picked = candidates[0]
      if (picked) {
        // Place them on schedule
        const { data: r1 } = await supabase.from('worker_schedule')
          .select('id').eq('user_id', picked.id).eq('schedule_date', today).maybeSingle()
        if (r1?.id) {
          await supabase.from('worker_schedule').update({ status: 'scheduled', updated_at: new Date().toISOString() }).eq('id', r1.id)
        } else {
          await supabase.from('worker_schedule').insert({
            user_id: picked.id, schedule_date: today, status: 'scheduled', assigned_by: profile.id,
          })
        }
        // Record on the request
        await supabase.from('day_off_requests').update({
          replacement_id: picked.id, replacement_status: 'accepted',
        }).eq('id', req.id)
        replacementName = picked.full_name
        await notifyWorker({
          userId: picked.id,
          type: 'replacement_assigned',
          title: 'You\'re covering ' + (req.profiles?.full_name || 'a teammate'),
          message: fmtDate(req.request_date) + ' — you\'ve been scheduled to cover.',
          link: '/schedule?request=' + req.id,
          relatedId: req.id,
        })
      }
    }

    // Notify requester
    await notifyWorker({
      userId: req.user_id,
      type: 'day_off_approved',
      title: 'Day off approved',
      message: fmtDate(req.request_date) + (replacementName ? ' — ' + replacementName + ' will cover.' : ' (no replacement assigned)'),
      link: '/schedule?request=' + req.id,
      relatedId: req.id,
    })

    setBusyId(null)
    await load()
  }

  const deny = async (req) => {
    if (!canApprove) return
    if (!window.confirm('Deny this day off request?')) return
    setBusyId(req.id)
    await supabase.from('day_off_requests').update({
      status: 'denied',
      approved_by: profile.id,
      approved_at: new Date().toISOString(),
    }).eq('id', req.id)
    await notifyWorker({
      userId: req.user_id,
      type: 'day_off_denied',
      title: 'Day off denied',
      message: fmtDate(req.request_date),
      link: '/schedule?request=' + req.id,
      relatedId: req.id,
    })
    setBusyId(null)
    await load()
  }

  if (!canApprove) return null
  if (loading) return null
  if (requests.length === 0) return null

  return (
    <div style={styles.wrap}>
      <div style={styles.header} onClick={() => setCollapsed(!collapsed)}>
        <div style={styles.title}>
          PENDING DAY OFF REQUESTS
          <span style={styles.badge}>{requests.length}</span>
        </div>
        <div style={styles.toggle}>{collapsed ? '▸' : '▾'}</div>
      </div>
      {!collapsed && (
        <div style={styles.list}>
          {requests.map((r) => (
            <div key={r.id} style={styles.row}>
              <div style={styles.main}>
                <div style={styles.name}>{r.profiles?.full_name || 'Worker'}</div>
                <div style={styles.meta}>
                  <span style={styles.date}>{fmtDate(r.request_date)}</span>
                  {r.reason && <span style={styles.reason}>— {r.reason}</span>}
                </div>
                {r.replacement_status && r.replacement_status !== 'none' && (
                  <div style={styles.repStatus}>replacement: {r.replacement_status}</div>
                )}
              </div>
              <div style={styles.actions}>
                <button disabled={busyId === r.id} onClick={() => approve(r, false)} style={styles.approveBtn}>APPROVE</button>
                <button disabled={busyId === r.id} onClick={() => approve(r, true)} style={styles.approveAltBtn}>APPROVE + AUTO-COVER</button>
                <button disabled={busyId === r.id} onClick={() => deny(r)} style={styles.denyBtn}>DENY</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  wrap: { marginBottom: '20px', border: '1px solid rgba(201,162,74,0.3)', borderRadius: '4px', backgroundColor: 'rgba(201,162,74,0.04)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#c9a24a', display: 'flex', alignItems: 'center', gap: '10px' },
  badge: { backgroundColor: '#c9a24a', color: '#0a0f1a', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700 },
  toggle: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 12px 12px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', flexWrap: 'wrap' },
  main: { flex: 1, minWidth: '200px' },
  name: { fontSize: '14px', color: '#ffffff', fontWeight: 500 },
  meta: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  date: { color: '#b48c3c', marginRight: '6px' },
  reason: { fontStyle: 'italic' },
  repStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', marginTop: '3px' },
  actions: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  approveBtn: { backgroundColor: '#4a8c6a', border: 'none', borderRadius: '3px', padding: '8px 12px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  approveAltBtn: { backgroundColor: 'transparent', border: '1px solid #4a8c6a', borderRadius: '3px', padding: '8px 12px', color: '#6fbf8e', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
  denyBtn: { backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', padding: '8px 12px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
}
